import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Album, Archive, Award, Axe, Backpack, BaggageClaim, Box, BookOpen, Bookmark, Book, Briefcase, Brush, Camera, Cherry, Clock, Cloud, Coffee, Compass, Cookie, Crown, Database, Diamond, Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, Droplet, Egg, FileText, Film, Flag, Folder, Gift, Globe, Hammer, Heart, Hexagon, Image, Key, Lamp, Leaf, Lock, Map, MessageCircle, Moon, Music, Package, Palette, Pencil, PenTool } from "lucide-react";
import type { FunnelData } from "../../types";
import { LucideIcon } from "lucide-react";

// Extended icon list with 50 object and symbol icons from Lucide
const EXTENDED_LOGO_ICONS: Array<{
  icon: LucideIcon;
  label: string;
  keywords: string;
}> = [{
  icon: Album,
  label: "Album",
  keywords: "music record disk collection"
}, {
  icon: Archive,
  label: "Archive",
  keywords: "box storage file"
}, {
  icon: Award,
  label: "Award",
  keywords: "trophy medal achievement"
}, {
  icon: Axe,
  label: "Axe",
  keywords: "tool cut wood"
}, {
  icon: Backpack,
  label: "Backpack",
  keywords: "bag travel school"
}, {
  icon: BaggageClaim,
  label: "Baggage",
  keywords: "luggage suitcase travel"
}, {
  icon: Book,
  label: "Book",
  keywords: "read education study"
}, {
  icon: BookOpen,
  label: "Open Book",
  keywords: "read study education"
}, {
  icon: Bookmark,
  label: "Bookmark",
  keywords: "save mark favorite"
}, {
  icon: Box,
  label: "Box",
  keywords: "package container storage"
}, {
  icon: Briefcase,
  label: "Briefcase",
  keywords: "work business bag"
}, {
  icon: Brush,
  label: "Brush",
  keywords: "paint art design"
}, {
  icon: Camera,
  label: "Camera",
  keywords: "photo picture image"
}, {
  icon: Cherry,
  label: "Cherry",
  keywords: "fruit food sweet"
}, {
  icon: Clock,
  label: "Clock",
  keywords: "time watch schedule"
}, {
  icon: Cloud,
  label: "Cloud",
  keywords: "weather storage online"
}, {
  icon: Coffee,
  label: "Coffee",
  keywords: "drink beverage cup"
}, {
  icon: Compass,
  label: "Compass",
  keywords: "navigation direction map"
}, {
  icon: Cookie,
  label: "Cookie",
  keywords: "food sweet snack"
}, {
  icon: Crown,
  label: "Crown",
  keywords: "royal king queen"
}, {
  icon: Database,
  label: "Database",
  keywords: "storage data server"
}, {
  icon: Diamond,
  label: "Diamond",
  keywords: "gem jewel precious"
}, {
  icon: Dice1,
  label: "Dice One",
  keywords: "game gamble number"
}, {
  icon: Dice2,
  label: "Dice Two",
  keywords: "game gamble number"
}, {
  icon: Dice3,
  label: "Dice Three",
  keywords: "game gamble number"
}, {
  icon: Dice4,
  label: "Dice Four",
  keywords: "game gamble number"
}, {
  icon: Dice5,
  label: "Dice Five",
  keywords: "game gamble number"
}, {
  icon: Dice6,
  label: "Dice Six",
  keywords: "game gamble number"
}, {
  icon: Droplet,
  label: "Droplet",
  keywords: "water drop liquid"
}, {
  icon: Egg,
  label: "Egg",
  keywords: "food breakfast cooking"
}, {
  icon: FileText,
  label: "File",
  keywords: "document paper text"
}, {
  icon: Film,
  label: "Film",
  keywords: "movie video cinema"
}, {
  icon: Flag,
  label: "Flag",
  keywords: "banner mark country"
}, {
  icon: Folder,
  label: "Folder",
  keywords: "directory files storage"
}, {
  icon: Gift,
  label: "Gift",
  keywords: "present box package"
}, {
  icon: Globe,
  label: "Globe",
  keywords: "world earth planet"
}, {
  icon: Hammer,
  label: "Hammer",
  keywords: "tool build construct"
}, {
  icon: Heart,
  label: "Heart",
  keywords: "love like favorite"
}, {
  icon: Hexagon,
  label: "Hexagon",
  keywords: "shape geometry"
}, {
  icon: Image,
  label: "Image",
  keywords: "picture photo art"
}, {
  icon: Key,
  label: "Key",
  keywords: "lock security password"
}, {
  icon: Lamp,
  label: "Lamp",
  keywords: "light bulb idea"
}, {
  icon: Leaf,
  label: "Leaf",
  keywords: "nature plant eco"
}, {
  icon: Lock,
  label: "Lock",
  keywords: "security password private"
}, {
  icon: Map,
  label: "Map",
  keywords: "location navigation travel"
}, {
  icon: MessageCircle,
  label: "Message",
  keywords: "chat comment talk"
}, {
  icon: Moon,
  label: "Moon",
  keywords: "night dark mode"
}, {
  icon: Music,
  label: "Music",
  keywords: "note song audio"
}, {
  icon: Package,
  label: "Package",
  keywords: "box delivery shipping"
}, {
  icon: Palette,
  label: "Palette",
  keywords: "art color paint"
}];
interface IconSelectorProps {
  data: FunnelData;
  onUpdate: (updates: Partial<FunnelData>) => void;
}
export const IconSelector = ({
  data,
  onUpdate
}: IconSelectorProps) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedIcon = EXTENDED_LOGO_ICONS.find(icon => icon.label === data.logoConfig?.icon);
  const filteredIcons = searchTerm ? EXTENDED_LOGO_ICONS.filter(icon => icon.label.toLowerCase().includes(searchTerm.toLowerCase()) || icon.keywords.toLowerCase().includes(searchTerm.toLowerCase())) : EXTENDED_LOGO_ICONS;
  const handleIconSelect = (label: string) => {
    onUpdate({
      logoConfig: {
        ...data.logoConfig,
        icon: label
      }
    });
    setIsPickerOpen(false);
  };
  return <div className="space-y-2">
      <Label>Ikon</Label>
      <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start text-left font-normal">
            <div className="flex items-center gap-2">
              {selectedIcon && <selectedIcon.icon className="w-4 h-4" />}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" side="right">
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-2">
              <Search className="w-4 h-4 text-slate-400" />
              <Input placeholder="Search icons..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="h-8" />
            </div>
            <ScrollArea className="h-[300px] pr-4">
              <div className="grid grid-cols-4 gap-2">
                {filteredIcons.map(({
                icon: Icon,
                label
              }) => <Button key={label} variant="outline" size="sm" className="h-10 p-0 flex items-center justify-center" onClick={() => handleIconSelect(label)}>
                    <Icon className="w-4 h-4" />
                  </Button>)}
              </div>
            </ScrollArea>
          </div>
        </PopoverContent>
      </Popover>
    </div>;
};